import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  RefreshCw, Download, ShieldCheck, Search, AtSign,
  X, Shuffle, FilterX, ExternalLink, AlertCircle
} from 'lucide-react';
import { getFeedModel, listFeedModels, syncFeedPromotedModels, verifyFeedPromotedModels } from '../api/feed';
import { AdminFeedModelSummary, FeedSyncResponse, FeedVerificationResponse } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';
import PageHeader from '../components/shared/PageHeader';
import { EmptyState } from '../components/shared/EmptyState';
import { TableSkeleton } from '../components/shared/TableSkeleton';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { fmtCurrency, fmtNumber as fmt } from '../utils/format';
import { useUrlState } from '../hooks/useUrlState';

const FeedPage: React.FC = () => {
  const navigate = useNavigate();
  const [urlState, setUrlState] = useUrlState({ q: '' });
  const filter = urlState.q;
  const setFilter = (v: string) => setUrlState({ q: v });
  const [pinpoint, setPinpoint] = useState('');
  const [pinpointError, setPinpointError] = useState<string | null>(null);
  const [pinpointBusy, setPinpointBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<FeedSyncResponse | null>(null);
  const [verifyResult, setVerifyResult] = useState<FeedVerificationResponse | null>(null);

  const { data: models, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['feed', 'models'],
    queryFn: listFeedModels
  });

  const filtered = useMemo(() => {
    if (!models) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return models;
    return models.filter(m =>
      m.modelUsername?.toLowerCase().includes(q) || m.modelName?.toLowerCase().includes(q)
    );
  }, [models, filter]);

  const syncMutation = useMutation({
    mutationFn: syncFeedPromotedModels,
    onSuccess: (data) => { setActionError(null); setVerifyResult(null); setSyncResult(data); refetch(); },
    onError: () => { setSyncResult(null); setActionError('Sync failed. Try again.'); }
  });

  const verifyMutation = useMutation({
    mutationFn: verifyFeedPromotedModels,
    onSuccess: (data) => { setActionError(null); setSyncResult(null); setVerifyResult(data); refetch(); },
    onError: () => { setVerifyResult(null); setActionError('Verification failed. Try again.'); }
  });

  const runPinpoint = async () => {
    const u = pinpoint.trim();
    if (!u || pinpointBusy) return;
    setPinpointBusy(true); setPinpointError(null);
    try {
      await getFeedModel(u);
      navigate(`/feed/${encodeURIComponent(u)}`);
    } catch {
      setPinpointError(`@${u} not in swiper`);
    } finally {
      setPinpointBusy(false);
    }
  };

  const actions = (
    <>
      <button className="hf-btn" onClick={() => refetch()} disabled={isFetching}>
        <RefreshCw size={14} /> Refresh
      </button>
      <button className="hf-btn" onClick={() => verifyMutation.mutate()} disabled={verifyMutation.isPending}>
        <ShieldCheck size={14} /> Verify models
      </button>
      <button className="hf-btn primary" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
        <Download size={14} /> Sync from base
      </button>
    </>
  );

  return (
    <>
      <PageHeader
        title="Swiper"
        subtitle={<><span className="hf-mono">{fmt(filtered.length)}</span> of <span className="hf-mono">{fmt(models?.length ?? 0)}</span> models</>}
        actions={actions}
      />

      <div className="hf-content">
        {/* Sync result strip */}
        {syncResult && (
          <ErrorBanner
            kind="success"
            title="Sync complete"
            message={
              <>
                Fetched <span className="hf-mono">{fmt(syncResult.fetched)}</span>,
                created <span className="hf-mono">{fmt(syncResult.created)}</span>.
              </>
            }
            onDismiss={() => setSyncResult(null)}
          />
        )}
        {verifyResult && (
          <ErrorBanner
            kind="info"
            title="Verification complete"
            message={
              <>
                Remote <span className="hf-mono">{fmt(verifyResult.remoteModels)}</span> ·
                Local <span className="hf-mono">{fmt(verifyResult.localModels)}</span> ·
                Deactivated <span className="hf-mono">{fmt(verifyResult.deactivated)}</span> ·
                Reactivated <span className="hf-mono">{fmt(verifyResult.reactivated)}</span>
              </>
            }
            onDismiss={() => setVerifyResult(null)}
          />
        )}
        {actionError && (
          <ErrorBanner
            kind="danger"
            message={actionError}
            onDismiss={() => setActionError(null)}
          />
        )}

        {/* Pinpoint search */}
        <div className="hf-feature-box">
          <div className="hf-label-row" style={{ marginBottom: 8 }}>
            <span className="hf-label">Open swiper model by username</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="hf-input" style={{ flex: 1 }}>
              <span className="hf-icon-l"><AtSign size={14} /></span>
              <input
                value={pinpoint}
                onChange={(e) => setPinpoint(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runPinpoint(); } }}
                placeholder="username"
              />
            </div>
            <button className="hf-btn primary" onClick={() => void runPinpoint()} disabled={pinpointBusy || pinpoint.trim().length === 0}>
              {pinpointBusy ? 'Searching…' : 'Open'}
            </button>
          </div>
          {pinpointError && (
            <div className="hf-help" style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={12} /> {pinpointError}
            </div>
          )}
        </div>

        {/* Filter toolbar */}
        <div className="hf-toolbar">
          <div className="hf-search" style={{ flex: '1 1 280px' }}>
            <Search size={14} />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter by @username or name"
              aria-label="Filter swiper models"
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter('')}
                aria-label="Clear filter"
                style={{ background: 'transparent', border: 'none', color: 'var(--fg-3)', cursor: 'pointer', padding: 2, display: 'inline-flex' }}
              >
                <X size={13} />
              </button>
            )}
          </div>
          <span className="hf-count">{fmt(filtered.length)} shown</span>
        </div>

        {/* Models table */}
        <div className="hf-card flush">
          {isLoading ? (
            <table className="hf-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>img</th>
                  <th>@username</th>
                  <th>name</th>
                  <th className="num">likes</th>
                  <th>price</th>
                  <th>onlyfans</th>
                </tr>
              </thead>
              <tbody>
                <TableSkeleton rows={6} columns={[6, 22, 30, 12, 14, 16]} numericColumns={[3]} />
              </tbody>
            </table>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 8 }}>
              <EmptyState
                Icon={filter ? FilterX : Shuffle}
                title={filter ? 'No models match the filter' : 'No swiper models yet'}
                description={
                  filter
                    ? 'Try a shorter query or clear the filter.'
                    : 'Run "Sync from base" to import promoted models into the swiper.'
                }
                action={
                  filter ? (
                    <button className="hf-btn" onClick={() => setFilter('')}>
                      <X size={13} /> Clear filter
                    </button>
                  ) : (
                    <button
                      className="hf-btn primary"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                    >
                      <Download size={13} /> Sync from base
                    </button>
                  )
                }
              />
            </div>
          ) : (
            <table className="hf-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>img</th>
                  <th>@username</th>
                  <th>name</th>
                  <th className="num">likes</th>
                  <th className="num">price</th>
                  <th>onlyfans</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m: AdminFeedModelSummary) => {
                  const url = pickImageUrl(m.imageUrls, m.imageFilename);
                  return (
                    <tr
                      key={m.id}
                      onClick={() => navigate(`/feed/${encodeURIComponent(m.modelUsername)}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ width: 32, height: 32, borderRadius: 4, overflow: 'hidden', background: 'var(--surface-2)' }}>
                          {url
                            ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <div className="hf-imgph" style={{ width: '100%', height: '100%' }} />}
                        </div>
                      </td>
                      <td><span className="hf-mono">@{m.modelUsername}</span></td>
                      <td>{m.modelName || '—'}</td>
                      <td className="num">{fmt(m.likesCount)}</td>
                      <td className="num">{fmtCurrency(m.price, { compactZero: true })}</td>
                      <td>
                        {m.onlyfansUrl
                          ? (
                            <a
                              href={m.onlyfansUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              onClick={(e) => e.stopPropagation()}
                              className="hf-mono"
                              style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              open <ExternalLink size={11} />
                            </a>
                          )
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedPage;
